/**
 * API Status Context
 *
 * Provides application-wide API availability state.
 * Components can use this to show offline indicators or gracefully degrade functionality.
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logger } from '../utils/logger';

interface ApiStatusContextValue {
  isApiAvailable: boolean;
  isLoading: boolean;
  setApiAvailable: (available: boolean) => void;
}

const ApiStatusContext = createContext<ApiStatusContextValue | undefined>(undefined);

interface ApiStatusProviderProps {
  children: ReactNode;
}

export function ApiStatusProvider({ children }: ApiStatusProviderProps): JSX.Element {
  const [isApiAvailable, setIsApiAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initial check complete after mount
    setIsLoading(false);
  }, []);

  const setApiAvailable = (available: boolean) => {
    if (isApiAvailable !== available) {
      setIsApiAvailable(available);
      logger.log(`API availability changed: ${available ? 'online' : 'offline'}`);
    }
  };

  return (
    <ApiStatusContext.Provider value={{ isApiAvailable, isLoading, setApiAvailable }}>
      {children}
    </ApiStatusContext.Provider>
  );
}

export function useApiStatus(): ApiStatusContextValue {
  const context = useContext(ApiStatusContext);
  if (!context) {
    throw new Error('useApiStatus must be used within ApiStatusProvider');
  }
  return context;
}
